import {
  index,
  pgTable,
  jsonb,
  text,
  uuid,
  varchar,
  pgEnum,
  boolean,
  integer,
} from 'drizzle-orm/pg-core';
import { PgAddress } from '@trashlab/api/src/domains/core/types';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { sql } from 'drizzle-orm';

/**
 * Enum defining the different types of organization sites.
 * - YARD: A storage or processing facility for materials and equipment
 * - DUMP: A disposal or waste management facility
 * - OFFSITE: A location not owned/operated by the organization but used for operations
 * - CUSTOM: A user-defined site type for special cases
 * - TRUCK_DEPOT: A facility for parking and maintaining trucks
 */
export const organization_site_type = pgEnum('organization_site_type', [
  'YARD',
  'DUMP',
  'OFFSITE',
  'CUSTOM',
  'TRUCK_DEPOT',
]);

/**
 * Represents a physical location or facility associated with an organization.
 * Used for tracking various operational sites like yards, dumps, and depots.
 * Each site has a specific type, location details, and identifying information.
 */
export const organization_site = pgTable(
  'organization_site',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** The type of site (yard, dump, etc.) */
    type: organization_site_type('type').notNull(),
    /** Human-readable name of the site */
    name: varchar('name', { length: 255 }).notNull(),
    /** Structured address information */
    address: jsonb('address').notNull().$type<PgAddress>(),
    /** Full address as a single string for easy display/search */
    addressString: text('addressString').notNull(),
    /** Site latitude for geolocation */
    latitude: varchar('latitude', { length: 20 }).notNull(),
    /** Site longitude for geolocation */
    longitude: varchar('longitude', { length: 20 }).notNull(),
    /** Short identifier code for quick reference */
    shortCode: varchar('shortCode', { length: 15 }).notNull(),
    /** IANA timezone identifier for the site (e.g., 'America/Los_Angeles') */
    timezone: varchar('timezone', { length: 64 })
      .default('America/Los_Angeles')
      .notNull(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /**
     * When true, this dump site will be auto-selected in the create order flow
     * if it's the closest dump site AND accepts the order's material.
     * Only applicable to sites with type='DUMP'.
     */
    autoSelectIfClosest: boolean('autoSelectIfClosest').default(false).notNull(),
    /**
     * Override duration in minutes for this organization site.
     * When set, this value is used instead of the default facility duration
     * in ETA calculations. Applies to any site type that drivers visit
     * (dump, yard, storage, etc.). This is the time spent on-site excluding travel time.
     */
    expectedDurationMinutes: integer('expectedDurationMinutes'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('organization_site_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('organization_site_tenantId_idx').on(table.tenantId),
      name_trgm_idx: index('organization_site_name_trgm_idx').using(
        'gin',
        table.name.op('gin_trgm_ops'),
      ),
      shortCode_trgm_idx: index('organization_site_shortCode_trgm_idx').using(
        'gin',
        table.shortCode.op('gin_trgm_ops'),
      ),
      addressString_trgm_idx: index(
        'organization_site_addressString_trgm_idx',
      ).using('gin', table.addressString.op('gin_trgm_ops')),
      tenant_type_name_id_idx: index(
        'organization_site_tenant_type_name_id_idx',
      ).on(table.tenantId, table.type, table.name, table.id),
      tenant_name_id_idx: index('organization_site_tenant_name_id_idx')
        .on(table.tenantId, table.name, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_yard_display_page_active_idx: index(
        'organization_site_tenant_yard_display_page_active_idx',
      )
        .on(
          table.tenantId,
          sql`lower(left(btrim(name), 48) COLLATE "C") COLLATE "C"`,
          sql`left(btrim(name), 48) COLLATE "C"`,
          sql`md5(btrim(name)) COLLATE "C"`,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.type} = 'YARD'::public.organization_site_type`,
        ),
      updatedAtTs_idx: index('organization_site_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('organization_site_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('organization_site_versionNumber_idx').on(
        table.versionNumber,
      ),
    };
  },
);

/**
 * Historical record of changes to organization sites.
 * Tracks all modifications to site records over time for auditing and versioning.
 * Contains the same fields as organization_site but allows nulls for partial updates.
 */
export const organization_site_history = pgTable('organization_site_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  type: organization_site_type('type'),
  name: varchar('name', { length: 255 }),
  address: jsonb('address').$type<PgAddress>(),
  addressString: text('addressString'),
  latitude: varchar('latitude', { length: 20 }),
  longitude: varchar('longitude', { length: 20 }),
  shortCode: varchar('shortCode', { length: 15 }),
  timezone: varchar('timezone', { length: 64 }),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  autoSelectIfClosest: boolean('autoSelectIfClosest'),
  expectedDurationMinutes: integer('expectedDurationMinutes'),
});
